import { Badge } from "./ui/Badge";
import { Card, CardBody, CardHeader } from "./ui/Card";
import { topic as topicCopy } from "../copy/sq";
import { confidenceDisplay } from "../lib/confidence";
import type { BriefingTopic } from "../types/api";

function Field({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">{label}</p>
      <p className="mt-0.5 text-sm leading-relaxed text-ink-700">{value}</p>
    </div>
  );
}

/**
 * One Section 4 investigation topic (MAIN or BONUS) — the one part of
 * the briefing that has a genuine structured shape (see
 * ai/parsers/daily_briefing_parser.py). `slot` (MAIN_1/MAIN_2/BONUS_1/
 * BONUS_2) is stable only within this one briefing, never across
 * dates — see ui/README.md's Phase 2 identity recommendation.
 */
export function TopicCard({ topic }: { topic: BriefingTopic }) {
  const confidence = confidenceDisplay(topic.confidence);
  const isMain = topic.topic_type === "MAIN";

  return (
    <Card>
      <CardHeader className="flex-col items-start gap-2 sm:flex-row sm:items-center">
        <div className="min-w-0">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <Badge tone={isMain ? "accent" : "neutral"}>{isMain ? topicCopy.main : topicCopy.bonus}</Badge>
            {topic.status ? <Badge tone="neutral">{topic.status}</Badge> : null}
            {topic.priority ? <Badge tone={topic.priority.toUpperCase() === "HIGH" ? "warn" : "neutral"}>{topic.priority}</Badge> : null}
          </div>
          <h3 className="font-serif text-base font-semibold leading-snug text-ink-950">{topic.title || topicCopy.untitled}</h3>
        </div>
        {confidence ? (
          <div className="flex shrink-0 items-center gap-2 sm:flex-col sm:items-end sm:gap-0.5">
            <Badge tone={confidence.tone}>{confidence.text}</Badge>
            {typeof topic.confidence_score === "number" ? (
              <span className="text-xs text-ink-400">{topicCopy.confidenceScore(topic.confidence_score)}</span>
            ) : null}
          </div>
        ) : null}
      </CardHeader>
      <CardBody className="space-y-3">
        <Field label={topicCopy.whyToday} value={topic.why_today ?? topic.why_worthwhile} />
        <Field label={topicCopy.centralQuestion} value={topic.main_question} />
        <Field label={topicCopy.whatWeKnow} value={topic.what_we_know} />
        <Field label={topicCopy.whatWeDontKnow} value={topic.what_we_do_not_know} />
        <Field label={topicCopy.whatToVerify} value={topic.what_to_verify} />
        <Field label={topicCopy.whoToContact} value={topic.contacts} />
        <Field label={topicCopy.suggestedQuestions} value={topic.suggested_questions} />
        <Field label={topicCopy.possibleAngle} value={topic.possible_angle} />
        <Field label={topicCopy.possibleOutcome} value={topic.possible_outcome} />
        <Field label={topicCopy.basis} value={topic.basis} />
      </CardBody>
    </Card>
  );
}
